'use client';

/**
 * BrazeIdentity — syncs the authenticated customer to Braze.
 *
 * Mirrors `PostHogIdentity` but gated on **marketing consent**. Calls
 * `brazeIdentify()` once when the customer ID changes AND the user has
 * granted advertising consent. The SDK loader (`Braze` component) will
 * only have set the SDK instance once marketing consent is granted, so
 * `brazeIdentify` is also a no-op when consent is denied — this
 * component is a defence-in-depth guard.
 *
 * **Why a separate component from `PostHogIdentity`?**
 *
 * PostHog identifies on analytics consent; Braze identifies on
 * marketing consent. These are independent categories in our granular
 * consent model (`analytics: true, advertising: false` is a real combo).
 * Keeping them in separate components keeps each gate explicit.
 */

import { brazeIdentify, isBrazeLoaded } from '@lib/analytics/braze';
import { hasMarketingConsent } from '@lib/analytics/consent';
import {
  BRAZE_LOADED_EVENT,
  CONSENT_UPDATED_EVENT,
} from '@lib/analytics/constants';
import { useEffect, useRef, useState } from 'react';
import { resolveIdentifyAction } from './resolve-identify-action';

type Props = {
  /** Medusa customer ID (when authenticated). */
  customerId?: string | null;
  /** Customer email for person properties. */
  customerEmail?: string | null;
  /** Customer first name. */
  customerFirstName?: string | null;
  /** Customer last name. */
  customerLastName?: string | null;
};

const BrazeIdentity = ({
  customerId,
  customerEmail,
  customerFirstName,
  customerLastName,
}: Props) => {
  const lastIdentifiedId = useRef<string | null>(null);
  const [consented, setConsented] = useState(false);
  // `brazeLoadedTick` is bumped whenever the SDK loader dispatches
  // `BRAZE_LOADED_EVENT`. We don't read its value — the state change
  // alone is what triggers the identify effect to re-run after the
  // async SDK import resolves.
  const [, setBrazeLoadedTick] = useState(0);

  // Track marketing consent reactively so we identify as soon as the
  // user opts in (without waiting for a page navigation).
  useEffect(() => {
    setConsented(hasMarketingConsent());

    const onConsentChange = () => {
      const next = hasMarketingConsent();
      // When marketing consent is withdrawn, `applyPreferences` calls
      // `brazeOptOut` which wipes the SDK profile (external_id +
      // device_id). The fresh Braze instance the loader creates on
      // a future re-opt-in is anonymous, so we must also forget which
      // customer we previously identified — otherwise the dedup guard
      // in `resolveIdentifyAction` would suppress the re-identify call
      // and the new instance would never get linked to the
      // logged-in customer.
      if (!next) {
        lastIdentifiedId.current = null;
      }
      setConsented(next);
    };
    window.addEventListener(CONSENT_UPDATED_EVENT, onConsentChange);

    // Listen for the SDK-loaded event so we can run identify once the
    // async dynamic import resolves — see the bail-out in the second
    // effect below.
    const onBrazeLoaded = () => setBrazeLoadedTick((n) => n + 1);
    window.addEventListener(BRAZE_LOADED_EVENT, onBrazeLoaded);

    return () => {
      window.removeEventListener(CONSENT_UPDATED_EVENT, onConsentChange);
      window.removeEventListener(BRAZE_LOADED_EVENT, onBrazeLoaded);
    };
  }, []);

  useEffect(() => {
    const action = resolveIdentifyAction({
      consented,
      customerId,
      lastIdentifiedId: lastIdentifiedId.current,
      customerEmail,
      customerFirstName,
      customerLastName,
    });

    if (action.kind === 'skip') {
      return;
    }

    // Only call `brazeIdentify` — and record the identification — when
    // the SDK is actually loaded. Consent state and the SDK-loaded
    // state are racy: `CONSENT_UPDATED_EVENT` fires synchronously from
    // `applyPreferences`, but the Braze loader's dynamic import is
    // async. So this effect can run with `consented=true` BEFORE
    // `setBrazeInstance` has registered the SDK. If we stamped the
    // ref now, `brazeIdentify` would no-op and the later SDK load
    // would not trigger a retry, leaving the logged-in Braze profile
    // anonymous. We instead bail; the effect re-runs on next render
    // or after the load completes (the SDK loader doesn't currently
    // emit a "loaded" event — see follow-up below).
    if (!isBrazeLoaded()) {
      return;
    }

    brazeIdentify(action.customerId, action.attrs);
    lastIdentifiedId.current = action.customerId;
  }, [
    consented,
    customerId,
    customerEmail,
    customerFirstName,
    customerLastName,
  ]);

  return null;
};

export default BrazeIdentity;
